/**
 * client/src/reader/WaitingBanner.tsx — the honest wait. §4.9, §7.5, LAW 20.
 *
 * ── The failure this component exists to prevent ────────────────────────────
 *
 * There are at least six reasons the narration might not be playing, and exactly one of them ends by
 * itself:
 *
 *   1. the GPU is generating this page (~30 s, and it will finish);
 *   2. it is generating somebody *else's* page and this one is behind it in a queue of one;
 *   3. spark-1 is unreachable, so nothing was even enqueued — §4.8 fails fast at enqueue on purpose;
 *   4. `AB_TTS_MODE=none` on this server, which is a supported state and not a fault;
 *   5. no worker is connected to the `tts` lane, so a queued row would be a promise nobody can keep;
 *   6. today's unattended GPU ceiling is spent, so nothing is being read *ahead* — though the page
 *      under your thumb still narrates on demand.
 *
 * A spinner means all six. Somebody sits in front of case 3 for ten minutes, taps, taps again, and
 * reloads — and on this product the reload is worse than the wait, because it can cost the queue
 * position that was already running. So this component never renders an indeterminate anything: it
 * names the state, and when it can put a number on it, the number comes from `estimateGpuMs` over the
 * characters that are actually missing (§2.4 — never a literal in this file, CI greps for `/ 11`,
 * `/ 14` and `* 56`).
 *
 * ── The number must be allowed to run out ───────────────────────────────────
 *
 * "about 25 seconds" that has been on screen for four minutes is worse than no estimate at all: it is
 * a promise the screen is visibly failing to keep, and it teaches people that the numbers here are
 * decoration. So the estimate counts **down**, and when it reaches zero the copy *changes* rather
 * than sitting at zero — first to "any moment now" inside the measured jitter, then to the honest
 * "the GPU is finishing another page first". `LANE_CONCURRENCY.tts = 1` because spark-1 has one GPU
 * and serialises anyway, so a queue in front of you is the designed state, not an incident.
 *
 * `OVERDUE_SLACK_MS` is that jitter and it is a measured number, not a feel: §4.8 assumed an
 * interactive request waits ~1.4 s to overtake background work, and P2's probe of the real box —
 * one GPU shared with llama.cpp at 27 GB, two vLLM engines and an ASR — measured the worst case at
 * **~7.7 s**. Eight seconds is that, rounded up once.
 *
 * ── ⚠️ NOT YET MOUNTED. It renders nowhere, and that is a gap, not a design ──
 *
 * `Reader.tsx` and `useNarration.ts` belong to other sessions this phase and nothing imports this
 * file yet. It is said here because an unmounted banner is invisible in exactly the way AGENTS.md
 * warns about — *"a 'not built yet' list goes stale in the direction nobody checks, because no screen
 * looks broken while it under-promises"* — and because `test/wiring.test.ts` scans `pages/` and
 * `routes/`, not `reader/`, so nothing would catch it.
 *
 * The whole integration, inside `Reader`'s narration region, above `PlaybackBar`:
 *
 *     <WaitingBanner
 *       state={narration.state}
 *       blocked={narration.blocked}          // `plan.blocked`, or `ensure`'s 503 body: {code, message}
 *       pendingChars={narration.pendingChars}
 *       startedAt={narration.waitStartedAt}  // `PageNote.askedAt` — the clock of the WAIT, not of the poll
 *       onNarrate={narration.onNarrate}
 *       onRetry={narration.onRetry}
 *     />
 *
 * `PlaybackBar`'s own `StallCopy` is the P2-era ancestor of this component and says four of the six
 * things below. When this lands, that branch should hand over rather than run beside it — two
 * components explaining the same wait in different words is the divergence §2.4 is about, one layer up.
 *
 * ── Styling, and the one timer ──────────────────────────────────────────────
 *
 * No stylesheet of its own: `reader-page.css` belongs to the reader frame and `ui/reader.css` is
 * lifted verbatim from books with a provenance header, so the layout here is inline and every colour
 * is a token. Mobile-first — it wraps at 390 px and never widens its container.
 *
 * ⚠️ The countdown is a **1 Hz interval that stops when the tab is hidden**, and that is not
 * politeness. §4.9's whole point is that this product runs with the screen locked for hours; a timer
 * left running is a wakeup per second for the entire night, and the read-along already owns the one
 * rAF loop this reader is allowed to have. When there is nothing to count — offline, failed, over
 * budget, not started — no timer is created at all.
 */

import { useEffect, useState, type CSSProperties, type ReactElement } from "react";

import { PAGE_TARGET_CHARS } from "../../../shared/doc.ts";
import type { PageState } from "../../../shared/narration/cue.ts";
import { estimateGpuMs } from "../../../shared/narration/rates.ts";

// ─── the numbers ─────────────────────────────────────────────────────────────

/**
 * How long past its own estimate a page may run before the copy stops promising and starts
 * explaining. Measured: ~7.7 s is the worst observed wait for an interactive request to overtake
 * other traffic on spark-1's single, shared GPU (P2's probe). Rounded up once.
 */
export const OVERDUE_SLACK_MS = 8_000;

/**
 * LAW 20's deadline, mirrored on the client so the banner tells the truth even if the room layer
 * forgets to.
 *
 * It is the same 120 s as `server/src/rooms/prefetch.ts`'s `ROOM_BLOCK_DEADLINE_MS`, and it is
 * deliberately duplicated rather than imported: this file is bundled by Vite and must not import
 * server code. What is NOT duplicated is the arithmetic behind it — both derive from
 * `estimateGpuMs`, in `shared/`, which is §2.4's one home for the rate. A caller that knows the
 * room's real deadline should pass it; this is the floor under a forgetful caller, not the authority.
 */
export const DEFAULT_DEADLINE_MS = 120_000;

// ─── what the banner is about ────────────────────────────────────────────────

/**
 * Everything the copy is derived from, and nothing else.
 *
 * It is a plain input rather than the server's `RoomWait` for the reason `CueSyncHost` is a plain
 * interface: the reader uses this component with a `NarrationPlan` and a room uses it with a
 * `RoomWait`, and neither should have to import the other's shape. `test/room-prefetch.test.ts`
 * holds a type-only assertion that a `RoomWait` still satisfies it, so the seam fails `npm run check`
 * rather than failing silently on a rename.
 */
export interface WaitingInput {
  /** The planner's answer for the page being waited on — not the one that is playing. */
  state: PageState;
  /** `plan.blocked`. The reason it is not merely slow. */
  blocked?: { code?: string | null; message?: string | null } | null;
  /** Characters this page still owes the GPU. The honest input to "about 25 seconds". */
  pendingChars?: number | null;
  /** `plan.estGpuSec`, when the server computed one. Beats `pendingChars`. */
  etaSec?: number | null;
  /** How long this wait has been on screen. */
  elapsedMs?: number | null;
  /** Runnable jobs in front of this one on the `tts` lane, when the caller knows. */
  queueAhead?: number | null;
  /** Room mode: whose page the room is blocked on. Empty or absent ⇒ it is ours. */
  waitingFor?: readonly string[];
  /** LAW 20. Defaults to `DEFAULT_DEADLINE_MS`; pass the room's own when you have it. */
  deadlineMs?: number | null;
  /** Set by the room when it has already dropped the block and moved on. Otherwise derived. */
  deadlinePassed?: boolean;
  /** Seconds of narration already generated ahead of here — "we will not stall again tonight". */
  audioSecReadyAhead?: number | null;
  /** The daily unattended ceiling is spent, so nothing is being read ahead. */
  budgetSpent?: boolean;
  /**
   * One listener, no room. Past the deadline there is no room that "went on" — the engine keeps
   * waiting for a page that is still being made, so the copy says that instead.
   */
  solo?: boolean;
}

/** Which of the six states this is. Rendered as `data-wait-tone`, so a gate can assert on it. */
export type WaitingTone = "ready" | "generating" | "queued" | "offline" | "budget" | "failed" | "silent";

export interface WaitingCopy {
  tone: WaitingTone;
  /** One sentence. Never empty except for `ready`, where the banner renders nothing at all. */
  headline: string;
  /** The second line: what it means for the person reading. */
  detail: string | null;
  /** The reassurance, when there is one to give. */
  aside: string | null;
  /** Which button belongs here, if any. The caller supplies the handler; absent handler, no button. */
  action: "narrate" | "retry" | null;
  /** True only while a countdown is running — i.e. the only case that needs a timer. */
  ticking: boolean;
}

// ─── the copy ────────────────────────────────────────────────────────────────

/**
 * State → words. Pure, exported, and tested directly, because this is the part that can be wrong in
 * a way no screenshot catches: copy that is *reassuring* about a state that will never resolve.
 *
 * The order of the branches is the design. `offline` outranks `generating` even when a job row
 * exists, because "generating — about 25 seconds" over an unreachable GPU is the exact sentence this
 * component was written to delete.
 */
export function waitingCopy(i: WaitingInput): WaitingCopy {
  const code = i.blocked?.code ?? null;
  const elapsedMs = Math.max(0, i.elapsedMs ?? 0);
  const deadlineMs = i.deadlineMs ?? DEFAULT_DEADLINE_MS;
  const aside = readyAheadAside(i.audioSecReadyAhead ?? null);

  if (i.state === "ready") {
    return { tone: "ready", headline: "", detail: null, aside: null, action: null, ticking: false };
  }

  // Not a wait at all: a full-page plate, a table, a page the structure editor marked silent. It must
  // never render as a stall, or every illustrated book looks broken on the same page every time.
  if (code === "nothing-to-say") {
    return {
      tone: "silent",
      headline: "There’s nothing here to read aloud.",
      detail: "An image, a table, or a page marked silent in the structure editor. The voice picks up on the next page.",
      aside: null,
      action: null,
      ticking: false,
    };
  }

  if (i.state === "failed") {
    return {
      tone: "failed",
      headline: "This page didn’t narrate.",
      detail: "Nothing was lost — retrying regenerates only what’s missing.",
      aside,
      action: "retry",
      ticking: false,
    };
  }

  if (code === "tts-mode-none") {
    return {
      tone: "offline",
      headline: "Narration is switched off on this server.",
      detail: "Everything else about the book works. Nothing is queued and nothing is waiting on it.",
      aside: null,
      action: null,
      ticking: false,
    };
  }

  if (code === "tts-unreachable") {
    // §4.9's own words, and the brief's: the state must be named, not spun.
    return {
      tone: "offline",
      headline: "The voice service is offline — you’re reading ahead of the narration.",
      detail: "Nothing was queued, so nothing is silently pending. Pages already narrated still play.",
      aside,
      action: "retry",
      ticking: false,
    };
  }

  if (code === "lane-unserved") {
    return {
      tone: "offline",
      headline: "The narrator isn’t running — you’re reading ahead of the narration.",
      detail: "Nothing is generating until it reconnects. Pages already narrated still play.",
      aside,
      action: null,
      ticking: false,
    };
  }

  if (i.state === "queued" || i.state === "building") {
    const totalSec = estimateSec(i);
    const remainingSec = totalSec - elapsedMs / 1000;

    // LAW 20: the block was dropped and the room went on. Saying so is the entire point of the law —
    // a banner that only a successful job can clear is a deadlock with a friendly face.
    if (i.deadlinePassed === true || elapsedMs > deadlineMs) {
      if (i.solo === true) {
        return {
          tone: "queued",
          headline: "Still making this page — the GPU is busy. It starts on its own when it’s ready.",
          detail: queueDetail(i.queueAhead ?? null),
          aside,
          action: null,
          ticking: false,
        };
      }
      return {
        tone: "queued",
        headline: "Still generating — the room went on without this page.",
        detail: "You’re reading ahead of the narration. It catches up on its own; nothing was lost.",
        aside,
        action: null,
        ticking: false,
      };
    }

    if (remainingSec > 0) {
      const who = nameList(i.waitingFor ?? []);
      return {
        tone: "generating",
        headline:
          who === null
            ? `Generating — about ${aboutSeconds(remainingSec)} seconds.`
            : `Waiting for ${who} — their page is generating.`,
        detail:
          who === null
            ? null
            : `About ${aboutSeconds(remainingSec)} seconds. The room starts again on its own.`,
        aside,
        action: null,
        ticking: true,
      };
    }

    if (elapsedMs <= totalSec * 1000 + OVERDUE_SLACK_MS) {
      return {
        tone: "generating",
        headline: "Generating — any moment now.",
        detail: null,
        aside,
        action: null,
        // Nothing left to count. A timer here would re-render once a second to redraw the same words.
        ticking: false,
      };
    }

    return {
      tone: "queued",
      headline: "Still generating — the GPU is finishing another page first.",
      detail: queueDetail(i.queueAhead ?? null),
      aside,
      action: null,
      ticking: false,
    };
  }

  // `missing` from here down: nothing is queued for this page.
  if (i.budgetSpent === true || code === "gpu-budget") {
    return {
      tone: "budget",
      headline: "Reading ahead is paused for today.",
      detail:
        "The day’s budget for generating pages nobody has opened yet is spent. " +
        "The page you’re on still narrates when you ask for it.",
      aside,
      action: "narrate",
      ticking: false,
    };
  }

  return {
    tone: "generating",
    headline: "This page hasn’t been narrated yet.",
    detail: `About ${aboutSeconds(estimateSec(i))} seconds of GPU, once — after that it plays instantly, for everyone.`,
    aside,
    action: "narrate",
    ticking: false,
  };
}

/**
 * What a page that owes the GPU nothing is still waiting for: a re-stitch.
 *
 * §4.11 measured one at **0.49 s** of CPU, and it runs on the `media` lane whose concurrency is 3, so
 * it is never behind a page of generation. Three seconds is that plus a queue, and it is here because
 * the alternative is worse in a way that is easy to miss: `pendingChars: 0` is not "we don't know", it
 * is "nothing is missing" — and falling through to the cold-page default would put *"about 25
 * seconds"* over half a second of work on every re-paginate, every codec change and every book that
 * was uploaded twice.
 */
const STITCH_SEC = 3;

/**
 * The seconds this page owes.
 *
 * `etaSec` when the server computed one; then the missing characters through §2.4's measured rate;
 * then a re-stitch when we are told nothing is missing; and only failing all three, a whole cold page
 * — the worst honest case rather than a number somebody copied out of a design document. A page whose
 * clips are all cache hits from another book must not claim twenty-five seconds it does not need.
 */
function estimateSec(i: WaitingInput): number {
  if (typeof i.etaSec === "number" && Number.isFinite(i.etaSec) && i.etaSec > 0) return i.etaSec;
  if (typeof i.pendingChars === "number" && Number.isFinite(i.pendingChars)) {
    return i.pendingChars > 0 ? estimateGpuMs(i.pendingChars) / 1000 : STITCH_SEC;
  }
  return estimateGpuMs(PAGE_TARGET_CHARS) / 1000;
}

/**
 * "about 25 seconds", not "about 27 seconds".
 *
 * Two significant figures on an estimate is a lie told precisely. Under ten seconds the person can
 * count along, so the rounding stops.
 */
export function aboutSeconds(sec: number): number {
  const s = Math.max(0, Math.round(sec));
  return s < 10 ? Math.max(1, s) : Math.round(s / 5) * 5;
}

function queueDetail(ahead: number | null): string {
  if (ahead === null || !Number.isFinite(ahead) || ahead <= 0) {
    return "One GPU, one page at a time — a queue in front of you is normal.";
  }
  return ahead === 1
    ? "One page is in front of this one."
    : `${ahead} pages are in front of this one.`;
}

/**
 * "You have about 8 minutes of narration in hand" — the number that answers *"is this going to keep
 * happening?"*, which is the question somebody actually has after the second banner of the evening.
 *
 * Below a minute it is not reassurance, it is noise, so it says nothing.
 */
function readyAheadAside(audioSec: number | null): string | null {
  if (audioSec === null || !Number.isFinite(audioSec) || audioSec < 60) return null;
  const mins = Math.round(audioSec / 60);
  return `About ${mins} minute${mins === 1 ? "" : "s"} of narration is already generated ahead of here.`;
}

/** "Dad", "Dad and Amma", "Dad and 2 others". Names are already sanitised by the room (§7.6). */
function nameList(names: readonly string[]): string | null {
  const clean = names.filter((n) => n.trim() !== "");
  if (clean.length === 0) return null;
  if (clean.length === 1) return clean[0] ?? null;
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  return `${clean[0]} and ${clean.length - 1} others`;
}

// ─── the component ───────────────────────────────────────────────────────────

export interface WaitingBannerProps extends Omit<WaitingInput, "elapsedMs"> {
  /**
   * When this wait began, as an epoch ms. The component derives `elapsedMs` from it and re-derives
   * once a second while a countdown is live — which is why the estimate is allowed to run out.
   *
   * ⚠️ It must be the clock of the **wait**, not of the poll. Re-stamping it every time the client
   * re-asks the planner makes `deadlinePassed` unreachable and puts "about 25 seconds" on screen
   * forever, which is the defect this whole file is arranged against. `useNarration`'s `PageNote`
   * carries exactly this field, `askedAt`, for exactly this reason.
   */
  startedAt?: number | null;
  /** `POST /pages/:id/narration/ensure`. Absent for a reader who may not spend GPU — no button. */
  onNarrate?: (() => void) | null;
  /** §4.11's Retry. Absent for a reader who may not spend GPU. */
  onRetry?: (() => void) | null;
  /** Tests and stories: freeze the clock. */
  now?: number;
}

const wrap: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "baseline",
  gap: "var(--space-1) var(--space-2)",
  paddingInline: "var(--space-3)",
  paddingBlock: "var(--space-2)",
  maxWidth: "100%",
  fontSize: "var(--text-xs)",
  lineHeight: 1.4,
  color: "var(--read-ink-muted)",
};

const headlineStyle: CSSProperties = {
  flex: "1 1 auto",
  minWidth: 0,
  fontWeight: 500,
};

const detailStyle: CSSProperties = {
  flex: "1 1 100%",
  minWidth: 0,
};

/**
 * The banner. Renders **nothing** when the page is ready — a component that renders an empty strip is
 * a component that shifts the text under somebody's thumb every time the GPU finishes a page.
 */
export function WaitingBanner(props: WaitingBannerProps): ReactElement | null {
  const { startedAt = null, onNarrate = null, onRetry = null, now, ...input } = props;

  // One state, bumped by the interval below. Nothing else in this component is stateful, and the
  // countdown is derived from the clock rather than stored, so a missed tick is invisible instead of
  // being a number that drifts away from the truth.
  const [, bump] = useState(0);

  const at = now ?? Date.now();
  const elapsedMs = startedAt === null ? (input.deadlinePassed === true ? Number.MAX_SAFE_INTEGER : 0) : at - startedAt;
  const copy = waitingCopy({ ...input, elapsedMs });

  useEffect(() => {
    // `now` is a frozen clock: a caller that passes one is driving time itself, and a timer would
    // fight it. Everything else with a live countdown gets exactly one interval, and only while the
    // tab is in front of somebody.
    if (!copy.ticking || now !== undefined || typeof document === "undefined") return;

    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = (): void => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const sync = (): void => {
      if (document.visibilityState === "visible") {
        bump((n) => n + 1);
        if (timer === null) timer = setInterval(() => bump((n) => n + 1), 1_000);
      } else {
        stop();
      }
    };
    document.addEventListener("visibilitychange", sync);
    sync();
    return () => {
      stop();
      document.removeEventListener("visibilitychange", sync);
    };
  }, [copy.ticking, now]);

  if (copy.tone === "ready" || copy.headline === "") return null;

  const alarming = copy.tone === "offline" || copy.tone === "failed";

  return (
    <div
      // `role="status"` and a polite live region: it is information, never an alarm over somebody who
      // is in the middle of a sentence. `assertive` here would interrupt a screen reader mid-word
      // every time the GPU finished a page.
      role="status"
      aria-live="polite"
      // The gate reads this. `data-narration-state` on the playback bar is the page's state; this is
      // what the person was actually told about it, which is the thing that can be wrong.
      data-wait-tone={copy.tone}
      style={{ ...wrap, color: alarming ? "var(--warn)" : "var(--read-ink-muted)" }}
    >
      <span style={headlineStyle}>{copy.headline}</span>

      {copy.action === "narrate" && onNarrate ? (
        // `data-no-turn` keeps `usePageTurn` off this control: without it a tap here lands in a tap
        // zone on a phone and turns the page as well as spending the GPU.
        <button type="button" className="ab-btn ab-btn-sm" onClick={onNarrate} data-no-turn="">
          Narrate this page
        </button>
      ) : null}

      {copy.action === "retry" && onRetry ? (
        <button type="button" className="ab-btn ab-btn-sm" onClick={onRetry} data-no-turn="">
          Try again
        </button>
      ) : null}

      {copy.detail !== null ? <span style={detailStyle}>{copy.detail}</span> : null}
      {copy.aside !== null ? (
        <span style={{ ...detailStyle, opacity: 0.75 }}>{copy.aside}</span>
      ) : null}
    </div>
  );
}

export default WaitingBanner;
